import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ArrowLeft, Check, Play } from 'lucide-react'
import { arr, pick, text, timestamp } from './helpers'
import type { ActCopy, Chapter, GuideContent, GuideMediaUrlBuilder, GuidePublication } from './types'
import { GuideBoundary } from './GuideUnavailable'
import { useGuideOpen, useVideoTelemetry, type GuideTelemetry } from './telemetry'

function rich(value: unknown): ReactNode[] {
  return text(value).split(/(\*\*.*?\*\*|\[\[.*?\]\]|\*.*?\*)/g).filter(Boolean).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>
    if (part.startsWith('[[') && part.endsWith(']]')) return <span key={index} className="rounded bg-primary/10 px-1.5 py-0.5 font-medium text-primary">{part.slice(2, -2)}</span>
    if (part.startsWith('*') && part.endsWith('*')) return <em key={index}>{part.slice(1, -1)}</em>
    return <Fragment key={index}>{part}</Fragment>
  })
}

/** Rail height used for the first paint and for SSR, before the rail is measured. */
const RAIL_FALLBACK = 56

export interface GuideReaderProps {
  content: GuideContent
  publication: GuidePublication
  guideId: string
  locale: string
  backHref: string
  backLabel: string
  mediaUrl: GuideMediaUrlBuilder
  showReviewState?: boolean
  /**
   * Playback telemetry. OMITTED means record nothing — the Console renders this
   * same reader for approval previews, and an admin reviewing a guide must not
   * be counted as a client watching it.
   */
  telemetry?: GuideTelemetry
  /**
   * Play an act's film when it scrolls into view. The films carry no audio track
   * (they are encoded `-an`), so a muted autoplay needs no user gesture and two
   * films can never talk over each other. Ignored for anyone who asked for
   * reduced motion.
   */
  autoplay?: boolean
}

/**
 * The guide reader.
 *
 * Every act renders, stacked down the page, and each act's film pins beside its
 * own steps until the next act pushes it away. It deliberately does NOT switch
 * acts behind a tab rail: a guide is one ordered procedure, so hiding the step
 * just completed loses the thread, and swapping panels of different heights
 * moves the page under the reader.
 *
 * The rail is a scroll-spy, not a tablist — `aria-current="step"` says "you are
 * here in a sequence", where a tablist announces parallel alternatives.
 *
 * The rule the whole layout is built around: NEVER scroll for the reader unless
 * they asked. A rail click is a request. Clicking a timestamp is not — it seeks
 * the film where it already stands.
 */
export function GuideReader(props: GuideReaderProps) {
  return <GuideBoundary locale={props.locale}><ReaderBody {...props}/></GuideBoundary>
}

type GuideCopy = GuideContent['copy'][string]
type GuideUi = GuideContent['ui'][string]
/** An act with its copy resolved for the locale; acts without copy are dropped. */
type ResolvedAct = { id: string; mobile?: boolean; copy: ActCopy }

function ReaderBody({ content, publication, guideId, locale, backHref, backLabel, mediaUrl, showReviewState = false, telemetry, autoplay = true }: GuideReaderProps) {
  const root = useRef<HTMLElement>(null)
  const rail = useRef<HTMLElement>(null)
  const chips = useRef<HTMLOListElement>(null)
  const sections = useRef<(HTMLElement | null)[]>([])
  const films = useRef<(HTMLDivElement | null)[]>([])
  useGuideOpen(guideId, telemetry)

  const acts = useMemo(() => arr<GuideContent['acts'][number]>(content?.acts).flatMap((act, index): ResolvedAct[] => {
    const copy = pick<ActCopy>(act?.copy, locale)
    return copy ? [{ id: text(act.id) || `act-${index}`, mobile: act.mobile === true, copy }] : []
  }), [content, locale])
  const copy: Partial<GuideCopy> = pick<GuideCopy>(content?.copy, locale) ?? {}
  const ui: Partial<GuideUi> = pick<GuideUi>(content?.ui, locale) ?? {}
  const premise = arr<{ h: unknown; p: unknown }>(copy.premise).filter(item => !!item && typeof item === 'object')
  const rules = arr<{ tag: unknown; h: unknown; p: unknown }>(copy.rules).filter(item => !!item && typeof item === 'object')

  const [railHeight, setRailHeight] = useState(RAIL_FALLBACK)
  const [activeIndex, setActiveIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  const [reduced, setReduced] = useState(false)
  const [playing, setPlaying] = useState<string | null>(null)
  const [warm, setWarm] = useState<string[]>([])
  const [watched, setWatched] = useState<string[]>([])

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const apply = () => setReduced(media.matches)
    apply()
    media.addEventListener('change', apply)
    return () => media.removeEventListener('change', apply)
  }, [])

  // The rail is sticky, so its height is the offset every scroll target and
  // every pinned film has to clear. Measured, not hard-coded: it wraps to two
  // lines on a narrow screen.
  useEffect(() => {
    const element = rail.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const measure = () => setRailHeight(element.offsetHeight || RAIL_FALLBACK)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [acts.length])

  /**
   * Scroll-spy. Positions come from `getBoundingClientRect`, which is relative
   * to the viewport whatever is doing the scrolling, and the listener is in the
   * capture phase because `scroll` does not bubble — so this keeps working if a
   * host ever puts the reader inside its own scroll container.
   */
  useEffect(() => {
    const onScroll = () => {
      const line = (rail.current?.getBoundingClientRect().bottom ?? RAIL_FALLBACK) + 12
      let next = 0
      sections.current.forEach((section, index) => { if (section && section.getBoundingClientRect().top <= line) next = index })
      setActiveIndex(next)
      const first = sections.current[0]?.getBoundingClientRect()
      const last = sections.current[sections.current.length - 1]?.getBoundingClientRect()
      if (!first || !last) return
      const span = last.bottom - first.top
      setProgress(span <= 0 ? 0 : Math.min(1, Math.max(0, (line - first.top) / span)))
    }
    onScroll()
    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true })
      window.removeEventListener('resize', onScroll)
    }
  }, [acts.length])

  /**
   * One observer decides which single film plays: the most visible one. Letting
   * each film start itself makes two of them fight on a short viewport where
   * both are past the threshold. It also records which acts have been near the
   * viewport, which is what arms their `preload`.
   */
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const ratios = new Map<string, number>()
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const id = entry.target.getAttribute('data-act') || ''
        ratios.set(id, entry.isIntersecting ? entry.intersectionRatio : 0)
        if (entry.isIntersecting) setWarm(current => current.includes(id) ? current : [...current, id])
      })
      let best: string | null = null
      let bestRatio = 0.5
      ratios.forEach((ratio, id) => { if (ratio > bestRatio) { bestRatio = ratio; best = id } })
      setPlaying(best)
    }, { threshold: [0, 0.25, 0.5, 0.75, 1] })
    films.current.forEach(element => { if (element) observer.observe(element) })
    return () => observer.disconnect()
  }, [acts.length])

  // Keep the active chip in view horizontally — with scrollLeft, not
  // scrollIntoView, which would be free to move the page vertically too.
  useEffect(() => {
    const list = chips.current
    const chip = list?.children[activeIndex] as HTMLElement | undefined
    if (!list || !chip) return
    const before = chip.offsetLeft - 16
    const after = chip.offsetLeft + chip.offsetWidth - list.clientWidth + 16
    if (before < list.scrollLeft) list.scrollTo({ left: Math.max(0, before), behavior: reduced ? 'auto' : 'smooth' })
    else if (after > list.scrollLeft) list.scrollTo({ left: after, behavior: reduced ? 'auto' : 'smooth' })
  }, [activeIndex, reduced])

  const pauseOthers = useCallback((except?: HTMLVideoElement) => {
    root.current?.querySelectorAll('video').forEach(item => { if (item !== except) item.pause() })
  }, [])

  const register = useCallback((index: number, section: HTMLElement | null, film: HTMLDivElement | null) => {
    sections.current[index] = section
    films.current[index] = film
  }, [])

  const onWatched = useCallback((id: string) => {
    setWatched(current => current.includes(id) ? current : [...current, id])
  }, [])

  /** The only sanctioned scroll: the reader asked to go to an act. */
  const goToAct = (index: number) => {
    sections.current[index]?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' })
    setActiveIndex(index)
  }

  return <main ref={root} className="mx-auto max-w-6xl px-5 py-8 sm:px-8" style={{ '--guide-rail': `${railHeight}px` } as React.CSSProperties}>
    <a href={backHref} className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="size-4"/>{backLabel}</a>
    {showReviewState && <div className="mb-6 rounded-lg border bg-muted/40 px-4 py-3 text-sm"><strong>{locale === 'fr' ? 'Aperçu Console' : 'Console preview'}</strong> · {locale === 'fr' ? 'version de l’application' : 'application version'} {publication.seedVersion} · {locale === 'fr' ? 'révision' : 'revision'} {publication.revision} · {publication.approved ? (locale === 'fr' ? 'Approuvé' : 'Approved') : (locale === 'fr' ? 'En attente d’approbation' : 'Pending approval')}</div>}
    <header className="mb-10 max-w-4xl"><p className="text-xs font-semibold uppercase tracking-[.18em] text-primary">{text(copy.eyebrow)}</p><h1 className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl">{text(copy.title)}</h1><p className="mt-5 text-lg leading-8 text-muted-foreground">{text(copy.deck)}</p></header>
    {premise.length > 0 && <section className="mb-12 rounded-xl border bg-muted/30 p-6"><h2 className="mb-5 text-sm font-semibold uppercase tracking-wider">{text(ui.premiseLabel)}</h2><div className="grid gap-5 md:grid-cols-3">{premise.map((item, itemIndex) => <div key={text(item.h) || itemIndex}><h3 className="font-semibold">{text(item.h)}</h3><p className="mt-2 text-sm leading-6 text-muted-foreground">{rich(item.p)}</p></div>)}</div></section>}

    <nav ref={rail} aria-label={text(ui.railLabel) || undefined} className="sticky top-0 z-20 -mx-5 border-y bg-background/95 px-5 backdrop-blur sm:-mx-8 sm:px-8">
      <ol ref={chips} className="flex gap-2 overflow-x-auto py-3">{acts.map((act, index) => <li key={act.id} className="shrink-0">
        <button type="button" aria-current={activeIndex === index ? 'step' : undefined} onClick={() => goToAct(index)} className={`flex items-center gap-2 rounded-full border py-1.5 pl-1.5 pr-3 text-xs font-medium transition ${activeIndex === index ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:border-primary hover:text-foreground'}`}>
          <span className={`grid size-5 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${activeIndex === index ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>{watched.includes(act.id) ? <Check className="size-3"/> : index + 1}</span>
          {text(act.copy.rail)}
        </button>
      </li>)}</ol>
      <div className="-mx-5 h-0.5 bg-muted sm:-mx-8"><div className="h-full bg-primary transition-[width] duration-150 ease-linear" style={{ width: `${Math.round(progress * 100)}%` }}/></div>
    </nav>

    <div>{acts.map((act, index) => <Act
      key={act.id}
      act={act}
      index={index}
      locale={locale}
      uiHint={text(ui.chapterHint)}
      publication={publication}
      guide={guideId}
      mediaUrl={mediaUrl}
      pauseOthers={pauseOthers}
      telemetry={telemetry}
      shouldPlay={autoplay && !reduced && playing === act.id}
      warm={warm.includes(act.id)}
      onWatched={onWatched}
      register={register}
    />)}</div>

    {rules.length > 0 && <section className="mt-16"><h2 className="text-2xl font-semibold">{text(copy.rulesTitle)}</h2><div className="mt-5 grid gap-4 md:grid-cols-3">{rules.map((rule, ruleIndex) => <div key={text(rule.h) || ruleIndex} className="rounded-xl border p-5"><span className="text-xs font-semibold uppercase tracking-wider text-primary">{text(rule.tag)}</span><h3 className="mt-2 font-semibold">{text(rule.h)}</h3><p className="mt-2 text-sm leading-6 text-muted-foreground">{rich(rule.p)}</p></div>)}</div></section>}
    {!!text(copy.footer) && <footer className="mt-16 border-t pt-6 text-xs text-muted-foreground">{rich(copy.footer)}</footer>}
  </main>
}

interface ActProps {
  act: ResolvedAct
  index: number
  locale: string
  uiHint: string
  publication: GuidePublication
  guide: string
  mediaUrl: GuideMediaUrlBuilder
  pauseOthers: (except?: HTMLVideoElement) => void
  telemetry?: GuideTelemetry
  shouldPlay: boolean
  warm: boolean
  onWatched: (id: string) => void
  register: (index: number, section: HTMLElement | null, film: HTMLDivElement | null) => void
}

function Act({ act, index, locale, uiHint, publication, guide, mediaUrl, pauseOthers, telemetry, shouldPlay, warm, onWatched, register }: ActProps) {
  const copy = act.copy
  const steps = useMemo(() => arr(copy.steps).filter((step): step is string => typeof step === 'string'), [copy.steps])
  const chapters = useMemo(() => arr<Chapter>(copy.chapters).filter(chapter => !!chapter && typeof chapter === 'object').map(chapter => ({ ...chapter, t: typeof chapter.t === 'number' && Number.isFinite(chapter.t) ? chapter.t : 0 })), [copy.chapters])
  const section = useRef<HTMLElement>(null)
  const film = useRef<HTMLDivElement>(null)
  const video = useRef<HTMLVideoElement>(null)
  const [time, setTime] = useState(0)
  const activeIndex = useMemo(() => { let current = -1; chapters.forEach((chapter, chapterIndex) => { if (chapter.t <= time + 0.25) current = chapterIndex }); return current }, [chapters, time])
  const track = useVideoTelemetry(video, act.id, telemetry)

  useEffect(() => { register(index, section.current, film.current) }, [index, register])
  useEffect(() => { video.current?.pause(); video.current?.load(); setTime(0) }, [locale])

  // Films are fetched only once they are worth fetching: `preload="none"` until
  // the act comes near, then metadata, then the bytes when it plays. The poster
  // and the reserved aspect box hold the exact space either way, so nothing on
  // the page moves when the media lands.
  useEffect(() => {
    const element = video.current
    if (element && warm && element.preload === 'none') element.preload = 'metadata'
  }, [warm])

  const start = useCallback((element: HTMLVideoElement) => {
    element.muted = true
    if (element.preload === 'none') element.preload = 'metadata'
    void element.play().catch(() => {})
  }, [])

  useEffect(() => {
    const element = video.current
    if (!element) return
    if (shouldPlay) { pauseOthers(element); start(element); return }
    if (element.paused) return
    // Moved on to another act rather than stopped watching — which is what
    // `reason: 'sequence'` is for. The `pause` event that follows carries
    // nothing new and is dropped as an empty report.
    track.flush('sequence')
    element.pause()
  }, [shouldPlay, pauseOthers, start, track])

  const seek = (seconds: number) => {
    const element = video.current
    if (!element) return
    pauseOthers(element)
    const apply = () => { element.currentTime = Math.max(0, seconds) }
    // With `preload="none"` there is no timeline to seek yet, so queue it.
    if (element.readyState >= 1) apply()
    else element.addEventListener('loadedmetadata', apply, { once: true })
    start(element)
    // NO scrollIntoView: the film is pinned beside these steps already, and
    // moving the page here is exactly the jump this layout exists to remove.
  }

  const caption = text(copy.caption) || [text(copy.title), text(copy.who)].filter(Boolean).join(' · ')
  const frame = act.mobile
    ? 'mx-auto aspect-[390/844] w-full max-w-[17rem] overflow-hidden rounded-[2rem] border-[10px] border-zinc-900 bg-zinc-900 shadow-lg'
    : 'aspect-[1280/800] overflow-hidden rounded-xl border bg-black shadow-sm'

  // The underscores are Tailwind's escape for the spaces `calc()` requires
  // around its operators — `calc(a+b)` is not valid CSS.
  return <section ref={section} id={`guide-act-${act.id}`} aria-labelledby={`guide-act-title-${act.id}`} className="grid scroll-mt-[calc(var(--guide-rail)_+_0.5rem)] gap-8 pt-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.12fr)]">
    <div className="order-2 min-w-0 lg:order-1">
      <p className="text-xs font-semibold uppercase tracking-wider text-primary">{text(copy.eyebrow)}</p>
      <h2 id={`guide-act-title-${act.id}`} className="mt-2 text-2xl font-semibold">{text(copy.title)}</h2>
      {!!text(copy.who) && <p className="mt-2 text-sm text-muted-foreground">{text(copy.who)}</p>}
      <p className="mt-4 leading-7 text-muted-foreground">{text(copy.intro)}</p>
      <p className="mt-5 text-xs italic text-muted-foreground">{uiHint}</p>
      <ol className="mt-2 space-y-2">{steps.map((step, stepIndex) => {
        const chapter = chapters[Math.min(stepIndex, chapters.length - 1)]
        const isActive = stepIndex === activeIndex
        return <li key={text(chapter?.id) || `${stepIndex}-${chapter?.t || 0}`}><button type="button" onClick={() => seek(chapter?.t || 0)} className={`grid w-full grid-cols-[3.25rem_1fr] gap-3 rounded-lg border p-3 text-left text-sm leading-6 transition ${isActive ? 'border-primary bg-primary/10 shadow-sm' : 'border-transparent hover:border-primary/40 hover:bg-muted/50'}`} aria-current={isActive ? 'step' : undefined}><span className={`mt-0.5 inline-flex items-center gap-1 font-mono text-xs tabular-nums ${isActive ? 'font-semibold text-primary' : 'text-muted-foreground'}`}><Play className="size-3"/>{timestamp(chapter?.t || 0)}</span><span className="flex gap-2"><span className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>{stepIndex + 1}</span><span><span className="mb-0.5 block text-xs font-semibold text-foreground">{text(chapter?.label)}</span>{rich(step)}</span></span></button></li>
      })}</ol>
      <div className="mt-6 rounded-r-lg border-l-4 border-amber-600 bg-amber-50 p-4 text-sm leading-6 text-amber-950 dark:bg-amber-950/30 dark:text-amber-100">{rich(copy.takeaway)}</div>
      <span className="sr-only">{index + 1}</span>
    </div>
    <div ref={film} data-act={act.id} className="order-1 min-w-0 pb-4 lg:order-2 lg:pb-14">
      <div className="lg:sticky lg:top-[calc(var(--guide-rail)_+_0.75rem)]">
        <div className={frame}>
          <video
            ref={video}
            className="block size-full bg-black object-contain"
            controls
            muted
            playsInline
            preload="none"
            poster={mediaUrl(publication, `${guide}/media/${locale}/${act.id}.jpg`)}
            src={mediaUrl(publication, `${guide}/media/${locale}/${act.id}.mp4`)}
            onTimeUpdate={event => { setTime(event.currentTarget.currentTime); track.onTimeUpdate(event.currentTarget.currentTime) }}
            onPlay={event => pauseOthers(event.currentTarget)}
            onSeeked={event => track.onSeek(event.currentTarget.currentTime)}
            onPause={() => track.flush('pause')}
            onEnded={() => { track.flush('ended'); onWatched(act.id) }}
          />
        </div>
        <p className="mt-3 text-xs leading-5 text-muted-foreground">{caption}</p>
      </div>
    </div>
  </section>
}
